// تب ارائه‌دهنده SCR-STAFF-921 (US-IAM-021، 025): ایجاد برای عضو فعال دارای پروفایل (BR-IAM-020)، نوع، تخصص‌ها از
// MEDICAL_SPECIALTIES با جستجو (AC-IAM-021.1)، عنوان و بیوگرافی عمومی، نمایش عمومی پیش‌فرض خاموش (AC-IAM-021.2)، فعال‌سازی و
// غیرفعال‌سازی با نمایش مصرف سقف limit.active_providers (AC-IAM-025.1 ← PHX-PLT-0402 با UpgradeBadge).
//
// AP-MED-03 (US-IAM-083): مقطع تحصیلی از داده مرجع ACADEMIC_DEGREES و هشدار «برای سیب ناقص» از
// `nationalReferralMissing` همان نما (AC-IAM-083.2) — سامانه ارجاع ملی بی این دو، ظرفیت پزشک را نمی‌پذیرد.
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  ErrorCodes,
  isApiError,
  unwrap,
  type CreateProviderRequest,
  type MembershipView,
  type ProviderType,
  type ProviderView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ErrorState,
  LoadingState,
  SampleBadge,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { useController, useForm, type Control } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import {
  iamKeys,
  LimitReachedAlert,
  useAcademicDegrees,
  useLimitLabel,
  useLimitUsage,
  useSpecialties,
} from './common';
import { ProviderCredentialsSection } from './ProviderCredentialsSection';
import { ProviderInsurerIdentifiersSection } from './ProviderInsurerIdentifiersSection';

/** انواع ارائه‌دهنده (AC-IAM-021.1). */
export const PROVIDER_TYPES: readonly ProviderType[] = [
  'Physician',
  'Dentist',
  'Therapist',
  'Technician',
  'Nurse',
  'Psychologist',
  'Nutritionist',
  'Midwife',
];

/** زبان‌های قابل انتخاب پروفایل عمومی (کد دوحرفی ISO 639-1 — validation.language). */
export const PROVIDER_LANGUAGES = ['fa', 'en', 'ar', 'tr', 'ku', 'az'] as const;

const providerSchema = z.object({
  type: z.enum(PROVIDER_TYPES as [ProviderType, ...ProviderType[]], { error: 'common:validation.required' }),
  specialtyCodes: z.array(z.string()).max(10, { error: 'iam:provider.specialties_max' }),
  title: z.string().max(100, { error: 'common:validation.too_long' }),
  publicBio: z.string().max(1000, { error: 'common:validation.too_long' }),
  languages: z.array(z.string()),
  publicVisible: z.boolean(),
  // AC-IAM-083.1: کد مقطع از مجموعه مرجع؛ تهی مجاز است اما ارجاع ملی را ناقص نگه می‌دارد.
  academicDegree: z.string(),
});
type ProviderForm = z.input<typeof providerSchema>;

const FIELDS = [
  'type',
  'specialtyCodes',
  'title',
  'publicBio',
  'languages',
  'publicVisible',
  'academicDegree',
] as const;

function SpecialtiesField({ control }: { control: Control<ProviderForm> }) {
  const { t } = useTranslation('iam');
  const specialties = useSpecialties();
  const { field, fieldState } = useController({ control, name: 'specialtyCodes' });
  const names = useMemo(
    () => new Map(specialties.options.map((o) => [o.key, o.name])),
    [specialties.options],
  );
  return (
    <Stack spacing={0.5}>
      <Autocomplete
        multiple
        options={specialties.options.map((option) => option.key)}
        value={field.value}
        loading={specialties.isPending}
        onChange={(_event, value) => field.onChange(value)}
        getOptionLabel={(key) => names.get(key) ?? key}
        filterSelectedOptions
        renderValue={(value, getItemProps) =>
          value.map((key, index) => {
            const { key: itemKey, ...itemProps } = getItemProps({ index });
            return <Chip key={itemKey} size="small" label={names.get(key) ?? key} {...itemProps} />;
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('iam:provider.specialties')}
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error?.message ? t(fieldState.error.message) : t('iam:provider.specialties_hint')
            }
            inputProps={{ ...params.inputProps, 'data-testid': 'provider-specialties' }}
          />
        )}
      />
      {specialties.isSample && (
        <Box>
          <SampleBadge />
        </Box>
      )}
    </Stack>
  );
}

/** مقطع تحصیلی (AC-IAM-083.1): تنها کدهای مجموعه مرجع؛ ورودی آزاد ساخته نمی‌شود تا PHX-IAM-0404 رخ ندهد. */
function AcademicDegreeField({ control }: { control: Control<ProviderForm> }) {
  const { t } = useTranslation('iam');
  const degrees = useAcademicDegrees();
  const { field, fieldState } = useController({ control, name: 'academicDegree' });
  const names = useMemo(
    () => new Map(degrees.options.map((option) => [option.key, option.name])),
    [degrees.options],
  );
  return (
    <Stack spacing={0.5}>
      <Autocomplete
        options={degrees.options.map((option) => option.key)}
        value={field.value === '' ? null : field.value}
        loading={degrees.isPending}
        onChange={(_event, value) => field.onChange(value ?? '')}
        getOptionLabel={(key) => names.get(key) ?? key}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('iam:provider.academic_degree')}
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error?.message
                ? t(fieldState.error.message)
                : t('iam:provider.academic_degree_hint')
            }
            inputProps={{ ...params.inputProps, 'data-testid': 'provider-academic-degree' }}
          />
        )}
      />
      {degrees.isSample && (
        <Box>
          <SampleBadge />
        </Box>
      )}
    </Stack>
  );
}

/** هشدار «برای سیب ناقص» (AC-IAM-083.2): فهرست از خود نما می‌آید، پس قاعده در یک جا می‌ماند. */
function NationalReferralGapAlert({ provider }: { provider: ProviderView }) {
  const { t } = useTranslation('iam');
  const missing = provider.nationalReferralMissing ?? [];

  if (missing.length === 0) {
    return null;
  }

  return (
    <Alert severity="warning" data-testid="provider-referral-gap" data-missing={missing.join(',')}>
      {t('iam:provider.referral_incomplete', {
        items: missing
          .map((reason) => t(`iam:provider.referral_reason.${reason}`, { defaultValue: reason }))
          .join(t('common:list_separator')),
      })}
    </Alert>
  );
}

function LanguagesField({ control }: { control: Control<ProviderForm> }) {
  const { t } = useTranslation('iam');
  const { field } = useController({ control, name: 'languages' });
  return (
    <Autocomplete
      multiple
      options={[...PROVIDER_LANGUAGES]}
      value={field.value}
      onChange={(_event, value) => field.onChange(value)}
      getOptionLabel={(code) => t(`iam:languages.${code}`, { defaultValue: code })}
      filterSelectedOptions
      renderInput={(params) => <TextField {...params} label={t('iam:provider.languages')} />}
    />
  );
}

function ProviderForm({
  member,
  provider,
  canWrite,
}: {
  member: MembershipView;
  provider: ProviderView | null;
  canWrite: boolean;
}) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const [saved, setSaved] = useState(false);
  const creating = provider === null;

  const values = useMemo<ProviderForm>(
    () => ({
      type: provider?.type ?? 'Physician',
      specialtyCodes: provider?.specialtyCodes ? [...provider.specialtyCodes] : [],
      title: provider?.title ?? '',
      publicBio: provider?.publicBio ?? '',
      languages: provider?.languages ? [...provider.languages] : ['fa'],
      // AC-IAM-021.2: نمایش عمومی به‌صورت پیش‌فرض خاموش.
      publicVisible: provider?.publicVisible ?? false,
      academicDegree: provider?.academicDegree ?? '',
    }),
    [provider],
  );
  const { control, handleSubmit, formState, setError } = useForm<ProviderForm>({
    resolver: zodResolver(providerSchema),
    values,
  });

  const save = useMutation({
    mutationFn: (form: ProviderForm) => {
      const body = {
        type: form.type,
        specialtyCodes: form.specialtyCodes,
        title: form.title.trim() || null,
        publicBio: form.publicBio.trim() || null,
        languages: form.languages,
        publicVisible: form.publicVisible,
        photoDocumentId: provider?.photoDocumentId ?? null,
        academicDegree: form.academicDegree.trim() || null,
      };
      if (creating) {
        const create: CreateProviderRequest = { ...body, membershipId: member.membershipId };
        return unwrap(
          tenantApi.POST('/api/v1/identity/providers', {
            body: create,
            headers: { 'Idempotency-Key': idempotency.current() },
          }),
        );
      }
      return unwrap(
        tenantApi.PUT('/api/v1/identity/providers/{id}', {
          params: { path: { id: provider.providerId } },
          body,
        }),
      );
    },
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: (view) => {
      queryClient.setQueryData(iamKeys.provider(session?.tenantId, view.providerId), view);
      void queryClient.invalidateQueries({ queryKey: iamKeys.all(session?.tenantId) });
      setSaved(true);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: FIELDS,
        codeFields: { 'PHX-IAM-0404': 'specialtyCodes' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <Box
      component="form"
      noValidate
      data-testid="provider-form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (canWrite) {
          void handleSubmit((form) => save.mutate(form))();
        }
      }}
    >
      <Stack spacing={2}>
        {saved && (
          <Alert severity="success" data-testid="provider-saved">
            {t(creating ? 'iam:provider.created' : 'iam:provider.saved')}
          </Alert>
        )}
        <SelectRHF
          control={control}
          name="type"
          labelKey="iam:provider.type"
          required
          options={PROVIDER_TYPES.map((type) => ({ value: type, labelKey: `iam:provider.types.${type}` }))}
        />
        <SpecialtiesField control={control} />
        <AcademicDegreeField control={control} />
        <TextFieldRHF control={control} name="title" labelKey="iam:provider.title_field" fullWidth />
        <TextFieldRHF
          control={control}
          name="publicBio"
          labelKey="iam:provider.public_bio"
          multiline
          minRows={3}
          fullWidth
        />
        <LanguagesField control={control} />
        <CheckboxRHF
          control={control}
          name="publicVisible"
          labelKey="iam:provider.public_visible"
          helperKey="iam:provider.public_visible_hint"
        />
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!canWrite || save.isPending || (!creating && !formState.isDirty)}
            data-testid="provider-submit"
          >
            {t(creating ? 'iam:provider.create' : 'common:actions.save')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

/** فعال‌سازی و غیرفعال‌سازی با نمایش مصرف سقف (AC-IAM-025.1 تا 025.3). */
function ProviderActivation({ provider, canWrite }: { provider: ProviderView; canWrite: boolean }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const usage = useLimitUsage('limit.active_providers');
  const limitLabel = useLimitLabel();
  const active = provider.status === 'Active';

  const toggle = useMutation({
    mutationFn: () => {
      const options = { params: { path: { id: provider.providerId } } };
      return active
        ? unwrap(tenantApi.POST('/api/v1/identity/providers/{id}:deactivate', options))
        : unwrap(tenantApi.POST('/api/v1/identity/providers/{id}:activate', options));
    },
    onSuccess: (view) => {
      queryClient.setQueryData(iamKeys.provider(session?.tenantId, provider.providerId), view);
      void queryClient.invalidateQueries({ queryKey: iamKeys.all(session?.tenantId) });
      access.refetch();
    },
  });

  return (
    <Stack spacing={1.5} data-testid="provider-activation">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography>{t('iam:provider.status_label')}</Typography>
        <span data-testid="provider-status" data-status={provider.status}>
          <StatusChip
            tone={active ? 'active' : 'inactive'}
            labelKey={`iam:provider.status.${provider.status ?? 'Inactive'}`}
          />
        </span>
        {usage.known && (
          <Chip
            size="small"
            variant="outlined"
            data-testid="provider-usage"
            label={t('iam:provider.usage', { value: limitLabel(usage) })}
          />
        )}
      </Stack>
      <LimitReachedAlert
        error={toggle.error}
        limitKey="limit.active_providers"
        hintKey="iam:provider.limit_hint"
      />
      {toggle.error && !(isApiError(toggle.error) && toggle.error.code === ErrorCodes.limitReached) ? (
        <ErrorState error={toggle.error} compact />
      ) : null}
      <Box>
        <Button
          variant={active ? 'outlined' : 'contained'}
          color={active ? 'warning' : 'primary'}
          disabled={!canWrite || toggle.isPending}
          onClick={() => toggle.mutate()}
          data-testid={active ? 'provider-deactivate' : 'provider-activate'}
        >
          {t(active ? 'iam:provider.deactivate' : 'iam:provider.activate')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {t(active ? 'iam:provider.deactivate_hint' : 'iam:provider.activate_hint')}
      </Typography>
    </Stack>
  );
}

export function ProviderTab({ member }: { member: MembershipView }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('iam.provider.manage');
  const providerId = member.providerId;

  const provider = useQuery({
    queryKey: iamKeys.provider(session?.tenantId, providerId ?? 'none'),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/identity/providers/{id}', { params: { path: { id: providerId ?? '' } } }),
      ),
    enabled: providerId !== null && write.permitted,
  });

  let content;
  if (!write.permitted) {
    content = <Alert severity="info">{t('iam:provider.no_permission')}</Alert>;
  } else if (providerId === null) {
    if (member.status !== 'Active') {
      content = (
        <Alert severity="info" data-testid="provider-requires-active">
          {t('iam:provider.requires_active')}
        </Alert>
      );
    } else if (!member.hasProfile) {
      content = (
        <Alert severity="info" data-testid="provider-requires-profile">
          {t('iam:provider.requires_profile')}
        </Alert>
      );
    } else {
      content = (
        <Stack spacing={2}>
          <Typography color="text.secondary">{t('iam:provider.create_hint')}</Typography>
          <ProviderForm member={member} provider={null} canWrite={write.canWrite} />
        </Stack>
      );
    }
  } else if (provider.isPending) {
    content = <LoadingState rows={3} />;
  } else if (provider.error || !provider.data) {
    content = <ErrorState error={provider.error} onRetry={() => void provider.refetch()} />;
  } else {
    content = (
      <Stack spacing={3}>
        <ProviderActivation provider={provider.data} canWrite={write.canWrite} />
        <NationalReferralGapAlert provider={provider.data} />
        <ProviderForm member={member} provider={provider.data} canWrite={write.canWrite} />
        <ProviderCredentialsSection providerId={provider.data.providerId} canWrite={write.canWrite} />
        <ProviderInsurerIdentifiersSection providerId={provider.data.providerId} />
      </Stack>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="provider-tab">
      <Stack spacing={2}>
        <WriteNotice write={write} />
        {content}
      </Stack>
    </Paper>
  );
}
